import aguid from 'lib/aguid';

import isV5ChartDef from '../../types/visTypeCheckers/isV5ChartDef';
import { toFilterPlate } from '../../migrateScope';
import filterTypeCheckers from '../../components/FilterPlateForm/filterTypeCheckers';

// Insights "pick a value to explain": set only while the picker is armed. The
// builders check it FIRST — before the "Coming soon" alerts and before the popup
// fork — so any clickable value can be explained, even ones that can't be
// filtered on. `drillDown` is the filter the click WOULD have applied (absent
// when none could be built), `term` the clicked label as the chart reported it.
export type MaybeExplain = (args: {
  drillDown?: FixedFilter;
  term: string;
}) => void;

const buildOnKeywordDrillDown = ({
  chartDef,
  drillDowns,
  field,
  setDrillDowns,
  isLastDrillForField,
  maybeOpenPopupReport,
  maybeExplain,
  track,
}: {
  chartDef?: V5ChartDefinition;
  drillDowns: FixedFilter[];
  field: string;
  setDrillDowns: (drills: FilterPlateType[]) => void;
  isLastDrillForField: boolean;
  maybeOpenPopupReport?: (drillDown?: FixedFilter) => void;
  maybeExplain?: MaybeExplain;
  track: () => void;
}) => {
  const buildNewDrill = (term: string): FixedFilter => {
    const newDrill: FixedFilter = {
      id: aguid(),
      field,
      fieldType: 'text',
      mode: 'editing' as 'editing',
      keywordValues: [],
      isExclude: false,
      appliedByInteractionWithChartDefId: chartDef ? chartDef.id : undefined,
    };
    if (term === '(Blank)') {
      newDrill.keywordsExists = false;
    } else {
      newDrill.keywordValues = [term];
    }
    return newDrill;
  };

  return (term: string, ctrlKey?: boolean) => {
    if (maybeExplain) {
      maybeExplain({ drillDown: buildNewDrill(term), term });
      return;
    }
    track();
    if (
      (field === 'key' && chartDef?.trendByHourInterval) ||
      (field === 'dayOfWeek' && !!chartDef?.groupByDayOfWeek) ||
      (isV5ChartDef(chartDef) &&
        chartDef.dimensionA &&
        chartDef.dimensionA.rangeInput)
    ) {
      alert('Coming soon...');
      return;
    }
    const newDrill = buildNewDrill(term);

    if (maybeOpenPopupReport) {
      maybeOpenPopupReport(newDrill);
      return;
    }

    if (isLastDrillForField) {
      const currentDrill = drillDowns[drillDowns.length - 1];
      const { currentKeywordValues, currentKeywordsExists } = (() => {
        if (filterTypeCheckers.isTextFilter(currentDrill)) {
          return {
            currentKeywordValues: currentDrill.keywordValues,
            currentKeywordsExists: currentDrill.keywordsExists,
          };
        }
        return {
          currentKeywordValues: [],
          currentKeywordsExists: undefined,
        };
      })();

      const isDeselectingTerm = currentKeywordValues.includes(term);
      const isDeselectingBlanks =
        term === '(Blank)' && currentKeywordsExists === false;

      const updatedDrill = (() => {
        if (isDeselectingTerm) {
          return {
            ...currentDrill,
            keywordValues: currentKeywordValues.filter((t) => t !== term),
            keywordsExists: ctrlKey ? currentKeywordsExists : undefined,
          };
        } else if (isDeselectingBlanks) {
          return {
            ...currentDrill,
            keywordsExists: undefined,
            keywordValues: ctrlKey ? currentKeywordValues : [],
          };
        } else {
          return {
            ...currentDrill,
            keywordValues: ctrlKey ? [...currentKeywordValues, term] : [term],
            keywordsExists: ctrlKey ? currentKeywordsExists : undefined,
          };
        }
      })();
      const isDrillRemoved =
        updatedDrill.keywordValues &&
        updatedDrill.keywordValues.length === 0 &&
        updatedDrill.keywordsExists === undefined;
      if (isDrillRemoved) {
        setDrillDowns(
          drillDowns.filter((d) => d.id !== currentDrill.id).map(toFilterPlate),
        );
      } else {
        setDrillDowns(
          drillDowns
            .map((d) => {
              if (d.id === currentDrill.id) {
                return updatedDrill;
              }
              return d;
            })
            .map(toFilterPlate),
        );
      }
    } else {
      setDrillDowns([...drillDowns, newDrill].map(toFilterPlate));
    }
  };
};

export default buildOnKeywordDrillDown;
